import { useWebSocketConnection } from "../useWebSocketConnection.js";
import { startTransition, useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { GameAPI } from "@rosette/ts/src/business/api/GameAPI.js";
import { PacketOutJoinQueue } from "@rosette/ts/src/business/game/server/outbound/PacketOutJoinQueue.js";
import type { ClientError } from "@rosette/ts/src/business/game/error/ClientError.js";
import { PacketInJoinLobby } from "@rosette/ts/src/business/game/server/inbound/PacketInJoinLobby.js";
import { PacketInError } from "@rosette/ts/src/business/game/server/inbound/PacketInError.js";
import { PacketInQueueState } from "@rosette/ts/src/business/game/server/inbound/PacketInQueueState.js";
import type { QueueState } from "@rosette/ts/src/business/game/queue/QueueState.js";
import type { QueueType } from "@rosette/ts/src/business/game/queue/QueueType.js";
import { areQueueTypesEqual } from "@rosette/ts/src/business/game/queue/QueueType.js";
import { PacketInRemovedFromQueue } from "@rosette/ts/src/business/game/server/inbound/PacketInRemovedFromQueue.js";
import type { WebSocketConnection } from "@rosette/ts/src/business/game/server/WebSocketConnection.js";


export function useMatchmaking(
    joinLobby: (lobbyID: string) => void,
    gameAPI: GameAPI,
): {
    activeQueue: QueueState | null;
    requestedQueue: QueueType | null;
    setRequestedQueue: (queue: QueueType | null) => void;
    connected: boolean;
    connection: WebSocketConnection | null;
    error: ClientError | null;
} {
    const [activeQueue, setActiveQueue] = useState<QueueState | null>(null);
    const [requestedQueue, setRequestedQueue] = useState<QueueType | null>(null);
    const [error, setError] = useState<ClientError | null>(null);

    // Use useQuery to fetch the matchmaking WebSocket URL
    const { data: wsUrl } = useQuery({
        queryKey: ["matchmaking-ws-url"],
        queryFn: () => gameAPI.getMatchmakingWebSocketURL(),
        enabled: requestedQueue !== null,
        staleTime: Infinity,
        gcTime: Infinity,
    });

    const { connected, connection } = useWebSocketConnection(
        requestedQueue !== null ? wsUrl ?? null : null,
    );

    useEffect(() => connection?.addPacketListener((packet) => {
        if (packet instanceof PacketInJoinLobby) {
            joinLobby(packet.getLobbyID());

        } else if (packet instanceof PacketInError) {
            setError(packet.getError());

        } else if (packet instanceof PacketInQueueState) {
            startTransition(() => {
                setActiveQueue(packet.getQueueState());
                setError(null);
            });

        } else if (packet instanceof PacketInRemovedFromQueue) {
            setActiveQueue((currentQueue) => {
                if (currentQueue === null)
                    return currentQueue;
                if (!areQueueTypesEqual(packet.getQueueType(), currentQueue.queueType))
                    return currentQueue;

                // Queue type matches one from packet - mark that we're no longer queued.
                return null;
            });
        }
    }), [setActiveQueue, joinLobby, connection]);

    const sendJoinQueuePacket = useCallback((queueToJoin: QueueType) => {
        connection?.trySend(new PacketOutJoinQueue(
            queueToJoin.gameMode, queueToJoin.ruleset,
        ));
    }, [connection]);

    // Send packet to join queue after connecting.
    useEffect(() => connection?.addOpenListener(() => {
        setError(null);
        if (requestedQueue) {
            sendJoinQueuePacket(requestedQueue);
        }
    }), [setError, requestedQueue, connection, sendJoinQueuePacket]);

    // Send packet to join queue after user changes their queue selection.
    useEffect(() => {
        if (requestedQueue) {
            sendJoinQueuePacket(requestedQueue);
        } else {
            setActiveQueue(null);
        }
    }, [setActiveQueue, requestedQueue, sendJoinQueuePacket]);

    return {
        activeQueue,
        requestedQueue,
        setRequestedQueue,
        connected,
        connection,
        error,
    };
}
